/***
*
*   COUPONS PANEL
*   Manage discount coupons with CRUD operations
*
**********/

import { useState } from 'react';
import { Card, Button, Icon, Badge } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

const DURATION_OPTIONS = [
  { value: 'once', label: 'Once' },
  { value: 'repeating', label: 'Repeating' },
  { value: 'forever', label: 'Forever' },
];

export function CouponsPanel({ coupons, onSave, onDelete, loading }) {
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isNewCoupon, setIsNewCoupon] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const couponsList = coupons || {};
  const couponKeys = Object.keys(couponsList);

  const handleAddCoupon = () => {
    setEditingCoupon({
      id: '',
      name: '',
      percent_off: null,
      amount_off: null,
      currency: 'usd',
      duration: 'once',
      duration_in_months: null,
      max_redemptions: null,
      redeem_by: null,
    });
    setIsNewCoupon(true);
  };

  const handleEditCoupon = (couponKey) => {
    const coupon = couponsList[couponKey];
    setEditingCoupon({ ...coupon });
    setIsNewCoupon(false);
  };

  const handleCancelEdit = () => {
    setEditingCoupon(null);
    setIsNewCoupon(false);
  };

  const handleSaveCoupon = () => {
    if (!editingCoupon.name) {
      return;
    }
    
    if (!editingCoupon.percent_off && !editingCoupon.amount_off) {
      return;
    }

    onSave(editingCoupon, isNewCoupon);
    setEditingCoupon(null);
    setIsNewCoupon(false);
  };

  const handleFieldChange = (field, value) => {
    setEditingCoupon(prev => ({ ...prev, [field]: value }));
  };

  const formatDiscount = (coupon) => {
    if (coupon.percent_off) {
      return `${coupon.percent_off}% off`;
    }
    if (coupon.amount_off) {
      const symbol = coupon.currency === 'usd' ? '$' : coupon.currency.toUpperCase();
      return `${symbol}${(coupon.amount_off / 100).toFixed(2)} off`;
    }
    return 'No discount';
  };

  const formatDuration = (coupon) => {
    if (coupon.duration === 'once') return 'One-time use';
    if (coupon.duration === 'forever') return 'Forever';
    if (coupon.duration === 'repeating') {
      return `${coupon.duration_in_months || 0} months`;
    }
    return coupon.duration;
  };

  const getCouponStatus = (coupon) => {
    if (!coupon.valid) return { text: 'Invalid', color: 'red' };
    if (coupon.redeem_by && new Date(coupon.redeem_by * 1000) < new Date()) {
      return { text: 'Expired', color: 'red' };
    }
    if (coupon.max_redemptions && coupon.times_redeemed >= coupon.max_redemptions) {
      return { text: 'Fully Redeemed', color: 'orange' };
    }
    return { text: 'Active', color: 'green' };
  };

  const filteredCoupons = couponKeys.filter(key => {
    const coupon = couponsList[key];
    const name = (coupon.name || '').toLowerCase();
    const id = (coupon.id || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || id.includes(search);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <Icon image="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder="Search coupons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          text="Add New Coupon"
          icon="plus"
          color="green"
          disabled={true}
          tooltip="Adding new coupons is disabled. Manage coupons directly in Stripe."
          action={handleAddCoupon}
        />
      </div>

      {/* Coupon Editor */}
      {editingCoupon && (
        <Card>
          <div className={Style.settingsSection}>
            <h3 className={Style.settingsHeader}>
              {isNewCoupon ? 'Add New Coupon' : 'Edit Coupon'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className={Style.settingsCard + " md:col-span-2"}>
                <label className={Style.settingsLabel}>Coupon Name *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingCoupon.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="e.g., Black Friday Sale"
                />
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Discount Type</label>
                <select
                  className={Style.settingsInput}
                  value={editingCoupon.percent_off ? 'percent' : 'amount'}
                  onChange={(e) => {
                    if (e.target.value === 'percent') {
                      handleFieldChange('amount_off', null);
                    } else {
                      handleFieldChange('percent_off', null);
                    }
                  }}
                >
                  <option value="percent">Percentage</option>
                  <option value="amount">Fixed Amount</option>
                </select>
              </div>

              {editingCoupon.percent_off !== null && (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Percent Off (%)</label>
                  <input
                    type="number"
                    className={Style.settingsInput}
                    value={editingCoupon.percent_off ?? ''}
                    onChange={(e) => handleFieldChange('percent_off', e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="20"
                    min="0"
                    max="100"
                  />
                </div>
              )}

              {editingCoupon.amount_off !== null && (
                <>
                  <div className={Style.settingsCard}>
                    <label className={Style.settingsLabel}>Amount Off (cents)</label>
                    <input
                      type="number"
                      className={Style.settingsInput}
                      value={editingCoupon.amount_off ?? ''}
                      onChange={(e) => handleFieldChange('amount_off', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="1000"
                      min="0"
                    />
                    <p className="text-xs text-slate-500 mt-1">Amount in cents (e.g., 1000 = $10.00)</p>
                  </div>

                  <div className={Style.settingsCard}>
                    <label className={Style.settingsLabel}>Currency</label>
                    <select
                      className={Style.settingsInput}
                      value={editingCoupon.currency}
                      onChange={(e) => handleFieldChange('currency', e.target.value)}
                    >
                      <option value="usd">USD ($)</option>
                      <option value="eur">EUR (€)</option>
                      <option value="gbp">GBP (£)</option>
                    </select>
                  </div>
                </>
              )}

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Duration</label>
                <select
                  className={Style.settingsInput}
                  value={editingCoupon.duration}
                  onChange={(e) => handleFieldChange('duration', e.target.value)}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {editingCoupon.duration === 'repeating' && (
                <div className={Style.settingsCard}>
                  <label className={Style.settingsLabel}>Duration (Months)</label>
                  <input
                    type="number"
                    className={Style.settingsInput}
                    value={editingCoupon.duration_in_months ?? ''}
                    onChange={(e) => handleFieldChange('duration_in_months', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="3"
                    min="1"
                  />
                </div>
              )}

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Max Redemptions</label>
                <input
                  type="number"
                  className={Style.settingsInput}
                  value={editingCoupon.max_redemptions ?? ''}
                  onChange={(e) => handleFieldChange('max_redemptions', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Unlimited"
                  min="1"
                />
                <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited</p>
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Redeem By Date</label>
                <input
                  type="date"
                  className={Style.settingsInput}
                  value={editingCoupon.redeem_by ? new Date(editingCoupon.redeem_by * 1000).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFieldChange('redeem_by', e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null)}
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                text="Cancel"
                color="gray"
                outline
                action={handleCancelEdit}
              />
              <Button
                text={isNewCoupon ? 'Create Coupon' : 'Save Changes'}
                color="green"
                loading={loading}
                action={handleSaveCoupon}
                disabled={!editingCoupon.name || (!editingCoupon.percent_off && !editingCoupon.amount_off)}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Coupons Grid */}
      <div className={Style.addOnsGrid}>
        {filteredCoupons.length > 0 ? (
          filteredCoupons.map((couponKey) => {
            const coupon = couponsList[couponKey];
            const status = getCouponStatus(coupon);

            return (
              <Card key={couponKey} className={Style.addOnCard}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={Style.addOnName}>{coupon.name}</h4>
                      <Badge text={status.text} color={status.color} />
                    </div>
                    <p className="text-xs text-slate-400 font-mono">{coupon.id}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      icon="edit-2"
                      size="small"
                      color="blue"
                      outline
                      disabled={true}
                      tooltip="Editing coupons is disabled. Manage in Stripe."
                      action={() => handleEditCoupon(couponKey)}
                    />
                    <Button
                      icon="trash-2"
                      size="small"
                      color="red"
                      outline
                      disabled={true}
                      tooltip="Deleting coupons is disabled. Manage in Stripe."
                      action={() => onDelete(coupon.id)}
                    />
                  </div>
                </div>

                <div className={Style.addOnPrice}>{formatDiscount(coupon)}</div>

                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Icon image="clock" size={14} color="gray" />
                    <span>{formatDuration(coupon)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Icon image="repeat" size={14} color="gray" />
                    <span>
                      Redeemed: {coupon.times_redeemed || 0}
                      {coupon.max_redemptions ? ` / ${coupon.max_redemptions}` : ' / ∞'}
                    </span>
                  </div>

                  {coupon.redeem_by && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icon image="calendar" size={14} color="gray" />
                      <span>Expires: {new Date(coupon.redeem_by * 1000).toLocaleDateString()}</span>
                    </div>
                  )}

                  {coupon.restrictions && Object.keys(coupon.restrictions).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 font-medium mb-1">Restrictions:</p>
                      {coupon.restrictions.minimum_amount && (
                        <p className="text-xs text-slate-600">
                          Min: ${(coupon.restrictions.minimum_amount / 100).toFixed(2)}
                        </p>
                      )}
                      {coupon.restrictions.first_time_transaction && (
                        <p className="text-xs text-slate-600">First-time customers only</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400">
                    Created: {new Date(coupon.created * 1000).toLocaleDateString()}
                  </p>
                </div>
              </Card>
            );
          })
        ) : (
          <Card>
            <div className={Style.emptyState}>
              <Icon image="tag" size={48} className="mx-auto mb-4 text-slate-300" />
              <p className={Style.emptyStateText}>
                {searchTerm ? `No coupons found matching "${searchTerm}"` : 'No coupons configured yet'}
              </p>
              {!searchTerm && (
                <Button
                  text="Add Your First Coupon"
                  icon="plus"
                  color="green"
                  disabled={true}
                  tooltip="Adding new coupons is disabled. Manage coupons directly in Stripe."
                  action={handleAddCoupon}
                />
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}